"use client";
import { MapSearch } from "@/components";
import { CircleChevronRight, Search, ArrowLeft, X } from "lucide-react";

export function MapPanel({
  showSearch,
  setShowSearch,
  searchWord,
  setSearchWord,
  activeCategory,
  setActiveCategory,
  categories,
  filteredLocations,
  setHoveredLocation,
  hoveredLocation,
  setActivateBounce,
  zoomToSelectedLoc,
  toggleCategory,
}) {
  // console.log("categories", categories);

  function activeCategoryTitle() {
    return categories.find((category) => category.slug === activeCategory)
      ?.title;
  }

  return (
    <>
      <div className="flex flex-col pb-3 space-y-2 border-b-2 border-brand-red-gum lg:pb-6">
        <div className="flex items-center justify-between">
          <h1 className="level-2">Show Map</h1>
          <button
            className={
              activeCategory != "all" ? "opacity-20 pointer-events-none" : ""
            }
            onClick={() => setShowSearch(!showSearch)}
          >
            {!showSearch ? <Search size={24} /> : <X size={24} />}
          </button>
        </div>

        {showSearch && (
          <MapSearch setSearchWord={setSearchWord} searchWord={searchWord} />
        )}
      </div>
      {!showSearch ? (
        <div className="flex flex-col h-full py-6 overflow-y-scroll lg:pt-6 remove-scrollbars">
          <div className="flex items-center justify-between">
            <button
              className={`col-span-2 ${
                activeCategory == "all" ? "hidden" : ""
              }`}
              onClick={() => setActiveCategory("all")}
            >
              <div className="flex items-center space-x-2">
                <ArrowLeft size={24} />
                <h2 className="level-4">{activeCategoryTitle()}</h2>
              </div>
            </button>
          </div>

          <div
            className="grid grid-cols-3 px-3 pb-5 gap-x-3 gap-y-8 sm:px-4 animate-section-in"
            style={{ "--anim-delay": "200ms" }}
          >
            {activeCategory === "all" &&
              categories.map((category, index) => (
                <button
                  key={index}
                  data-name="category"
                  onClick={() => toggleCategory(category)}
                  className={`flex flex-col items-center justify-start w-full min-w-0 space-y-3 px-0.5 text-center group animate-card-in ${
                    activeCategory === category.slug
                      ? ""
                      : activeCategory == "all"
                      ? ""
                      : "hidden"
                  }`}
                  style={{ "--anim-delay": `${Math.min(index, 15) * 30}ms` }}
                >
                  <div className="flex items-center justify-center transition-all duration-300 bg-white border border-brand-fawn rounded-full size-14 shrink-0">
                    <div
                      className="flex items-center justify-center w-full h-full p-1 category-circle"
                      dangerouslySetInnerHTML={{ __html: category.icon }}
                    />
                  </div>
                  <p className="w-full text-xs leading-tight break-words text-foreground font-heading">
                    {category.title}
                  </p>
                </button>
              ))}
          </div>
          {activeCategory != "all" && (
            <div
              key={activeCategory}
              className={`flex flex-col w-full h-full space-y-2 overflow-y-auto remove-scrollbars animate-section-in`}
            >
              {filteredLocations.map((location, index) => {
                if (location.isFacility) {
                  if (
                    !location.facilities[0].long ||
                    !location.facilities[0].lat
                  )
                    return null;
                } else {
                  if (!location.long || !location.lat) return null;
                }

                const locationsSlug = location?.isFacility
                  ? location?.facilities[0].slug
                  : location.slug;

                return (
                  <button
                    className={`flex border rounded-base items-center justify-between p-3 transition-all duration-300 hover:bg-brand-red-gum hover:text-white ${
                      hoveredLocation == locationsSlug
                        ? "bg-brand-red-gum text-white"
                        : ""
                    }`}
                    onMouseOver={() => {
                      setHoveredLocation(locationsSlug);
                      setActivateBounce(true);
                    }}
                    onMouseOut={() => {
                      setHoveredLocation(null);
                      setActivateBounce(false);
                    }}
                    onClick={(e) =>
                      zoomToSelectedLoc(
                        e,
                        location?.isFacility
                          ? location?.facilities[0]
                          : location,
                        location,
                        index
                      )
                    }
                    key={index} // Add a unique key for each button
                  >
                    <p className="flex flex-col items-start font-heading">
                      {location?.isFacility
                        ? location?.facilities.map((facility) => (
                            <span key={facility.id}>{facility.title}</span>
                          ))
                        : location.title}
                    </p>
                    <CircleChevronRight />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div
          className={`flex flex-col w-full h-full space-y-2 overflow-y-auto remove-scrollbars animate-section-in`}
        >
          {filteredLocations.map((location, index) => {
            if (location.isFacility) {
              if (!location.facilities[0].long || !location.facilities[0].lat)
                return null;
            } else {
              if (!location.long || !location.lat) return null;
            }

            const locationsSlug = location?.isFacility
              ? location?.facilities[0].slug
              : location.slug;

            return (
              <button
                className={`flex items-center border rounded-base justify-between p-4 transition-all duration-300 hover:bg-brand-red-gum/10 animate-card-in ${
                  hoveredLocation == locationsSlug ? "bg-brand-red-gum/10" : ""
                }`}
                style={{ "--anim-delay": `${Math.min(index, 15) * 25}ms` }}
                onMouseOver={() => {
                  setHoveredLocation(locationsSlug);
                  setActivateBounce(true);
                }}
                onMouseOut={() => {
                  setHoveredLocation(null);
                  setActivateBounce(false);
                }}
                onClick={(e) =>
                  zoomToSelectedLoc(
                    e,
                    location?.isFacility ? location?.facilities[0] : location,
                    location,
                    index
                  )
                }
                key={index} // Add a unique key for each button
              >
                <p className="font-heading">
                  {location?.isFacility
                    ? location?.facilities[0].title
                    : location.title}
                </p>
                <CircleChevronRight />
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
